/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Ribbon from '@/components/molecules/ribbon/ribbon';
import variables from '@/components/organisms/inspirational-search-card/inspirational-search-card.variables';
import styles from './inspirational-search-card.module.scss';

export interface InspirationalSearchCardProps {
  /** Indicates whether to always show the image */
  alwaysShowImage?: boolean;
  /** The text to show as a headline */
  headline?: string;
  /** The image to show */
  image?: Content.Properties.Image;
  /** An event that gets triggered on click */
  onClick?: () => void;
  /** Indicates whether the card is selected */
  selected?: boolean;
  /** The text to show as a sub headline */
  subHeadline?: string;
}

const InspirationalSearchCard = ({
  alwaysShowImage = false,
  headline,
  image,
  onClick,
  selected = false,
  subHeadline,
}: InspirationalSearchCardProps) => {
  const renderContent = () => (
    <div className={styles.card}>
      <div className={styles.highlight}>
        <div className={styles.checkMarkWrapper}>
          <Icon name="CheckMark" size={24} />
        </div>
      </div>

      <div
        className={styles.cardContent}
        style={{
          maskImage: `url(/assets/modules/inspirational-search-flow/mask.svg), linear-gradient(#000 0 0)`,
        }}
      >
        <div className={styles.content}>
          {image && (
            <div className={styles.imageWrapper}>
              <Image
                alt={image.altText}
                aspectRatio={variables.imageAspectRatio}
                height={200}
                src={image.url}
                unoptimized
                width={200}
              />
            </div>
          )}

          <div className={styles.textWrapper}>
            <Typography tagStyle="bodyMediumBold">{headline}</Typography>

            {subHeadline && (
              <Typography tagStyle="bodySmall">{subHeadline}</Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <button
      className={classNames(styles.component, {
        [styles.hasClick]: !!onClick,
        [styles.hasImage]: image,
        [styles.hasImageAlwaysVisible]: alwaysShowImage,
        [styles.isSelected]: selected,
      })}
      onClick={onClick}
    >
      {image && <Ribbon className={styles.ribbonBottom} variant="bottom" />}

      {renderContent()}

      {image && <Ribbon className={styles.ribbonTop} variant="top" />}
    </button>
  );
};

InspirationalSearchCard.displayName = 'InspirationalSearchCard';

export default InspirationalSearchCard;
